import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Chip, Menu, MenuItem, Typography } from '@mui/material';
import { useMessages } from 'pages/trading/queryHooks';
import { differenceInDays, isToday, parseISO } from 'date-fns';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TradingMessageDisplay } from 'pages/trading/TradingMessageDisplay';
import { PageContainer, PageHeader, Tile } from 'components/Layout/PageContainer';
import { groupBy } from 'utils/groupBy';
import { CountrySelect } from 'components/filter/input/CountrySelect';
import { MarketplaceSelect } from 'components/filter/input/MarketplaceSelect';
import { CategorySelect } from 'components/filter/input/CategorySelect';
import { BrandSelect } from 'components/filter/input/BrandSelect';
import { InfoButton } from 'components/InfoButton';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { TradingMessagesInfoContent } from 'pages/trading/TradingMessagesInfoContent';
import { ClearTradingFilterButton } from 'pages/trading/ClearTradingFilterButton';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { TradingMessage } from 'pages/trading/model';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { getChipSxStylingForLevel } from 'styles/utils';
import { useAppStore } from 'hooks/state/useAppStore';
import { TradingEmptyState } from 'components/TradingEmptyState';
import { useReadMessagesStore, useUnreadMessageCountFiltered } from 'hooks/state/useReadMessagesStore';
import { useAuth0 } from '@auth0/auth0-react';
import { trackMatomoEvent } from 'services/matomo.service';
import { getErrorMessage } from 'utils/getErrorMessage';
import { cn } from 'utils/classNamesTailwind';
import { TbIcon } from 'components/Figma';
import { Button } from 'components/Button';

interface GroupedMessagesByRelativeDate {
  label: string;
  dateString: string;
  isLatest: boolean;
  messages: TradingMessage[];
}

interface MessageSection {
  isLatest: boolean;
  generatedDate?: string;
  groups: GroupedMessagesByRelativeDate[];
}

const getRelativeDateLabel = (dateString: string, intl: IntlShape): { label: string; daysAgo: number } => {
  const messageDate = parseISO(dateString);
  const today = new Date();

  if (isToday(messageDate)) {
    return {
      label: intl.formatMessage({ id: 'trading.message.latest' }),
      daysAgo: 0,
    };
  }

  const daysAgo = differenceInDays(today, messageDate);
  return {
    label: intl.formatMessage({ id: 'trading.messages.days.ago' }, { days: daysAgo }),
    daysAgo,
  };
};

interface EmptyStateProps {
  hasActiveFilters: boolean;
  onExploreInsights: () => void;
}

function EmptyState({ hasActiveFilters, onExploreInsights }: EmptyStateProps) {
  const intl = useIntl();

  if (hasActiveFilters) {
    return (
      <TradingEmptyState
        title={intl.formatMessage({
          id: 'trading.empty.filters_applied',
          defaultMessage: 'No messages found for the selected filters.',
        })}
      />
    );
  }

  const descriptionText = intl.formatMessage({
    id: 'trading.empty.no_generated_messages.description',
    defaultMessage:
      "We'll keep an eye on things and alert you if that changes. While you wait for tomorrow's update, why not exploring",
  });

  return (
    <TradingEmptyState
      title={intl.formatMessage({
        id: 'trading.empty.no_generated_messages',
        defaultMessage: 'No messages found for the Steering Hub BETA in the last 7 days.',
      })}
      description={
        <span>
          {descriptionText}{' '}
          <button
            onClick={onExploreInsights}
            className="underline hover:no-underline font-semibold text-text-primary cursor-pointer bg-none border-none p-0"
          >
            {intl.formatMessage({ id: 'insights', defaultMessage: 'Insights' })}
          </button>
        </span>
      }
      illustration="InProgress"
      testId="trading-messages-empty-state"
    />
  );
}

export function TradingMessages() {
  const intl = useIntl();
  const { isSuperAdmin, isAdminView, accountNumber } = useContext(TenantConfigContext);
  const { user } = useAuth0();
  const { brands, categories, countries, marketplaces } = useAppStore();

  const { data: messages, isPending, isError, isSuccess, error } = useMessages();
  const markAllMessagesAsRead = useReadMessagesStore(state => state.markAllMessagesAsRead);
  const unreadCount = useUnreadMessageCountFiltered(messages, isSuperAdmin && isAdminView);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [messageSections, setMessageSections] = useState<MessageSection[]>([]);

  const hasActiveFilters = useMemo(
    () =>
      (countries ?? []).length > 0 ||
      (categories ?? []).length > 0 ||
      (brands ?? []).length > 0 ||
      (marketplaces ?? []).length > 0,
    [countries, categories, brands, marketplaces]
  );

  useEffect(() => {
    if (messages) {
      const grouped = groupBy(messages ?? [], ({ currentDataVersion }) => currentDataVersion);
      const sortedDateKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

      const groupedByRelativeDate = new Map<string, GroupedMessagesByRelativeDate>();

      sortedDateKeys.forEach(dateKey => {
        const { label, daysAgo } = getRelativeDateLabel(dateKey, intl);
        const latest = daysAgo === 0;

        if (!(isSuperAdmin && isAdminView) && daysAgo > 7) {
          return;
        }

        if (!groupedByRelativeDate.has(label)) {
          groupedByRelativeDate.set(label, {
            label,
            dateString: dateKey,
            isLatest: latest,
            messages: [],
          });
        }

        const group = groupedByRelativeDate.get(label)!;
        group.messages.push(...grouped[dateKey]);
      });

      const allGroups = Array.from(groupedByRelativeDate.values());
      const latestGroup = allGroups.find(g => g.isLatest);
      const olderGroups = allGroups.filter(g => !g.isLatest).sort((a, b) => b.dateString.localeCompare(a.dateString));

      const sections: MessageSection[] = [];

      if (latestGroup) {
        sections.push({
          isLatest: true,
          generatedDate: latestGroup.dateString,
          groups: [latestGroup],
        });
      }

      if (olderGroups.length > 0) {
        sections.push({
          isLatest: false,
          groups: olderGroups,
        });
      }

      setMessageSections(sections);
    }
  }, [messages, intl, isSuperAdmin, isAdminView]);

  const { setContent } = useInfoDrawer();
  const navigate = useNavigate();

  useEffect(() => {
    const performanceDashboardInfo = {
      titleKey: 'trading.info.title',
      content: <TradingMessagesInfoContent />,
    };

    setContent(performanceDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearAllMessages = () => {
    if (messages && messages.length > 0) {
      const allMessageIds = messages.map(msg => msg.messageId);
      markAllMessagesAsRead(accountNumber, user?.sub, allMessageIds);
      trackMatomoEvent({
        category: 'trading',
        action: 'mark_all_messages_as_read',
        name: `messages_count_${allMessageIds.length}`,
      });
    }
    handleMenuClose();
  };

  return (
    <div>
      <PageHeader topRightComponent={<InfoButton tooltipText="View dashboard information" showText />}>
        <CountrySelect />
        <BrandSelect />
        <MarketplaceSelect />
        <CategorySelect />
        <ClearTradingFilterButton />
      </PageHeader>
      <div className="standard-page-container">
        <PageContainer className="gap-y-[14px]">
          {isError && <Tile errorDetails={getErrorMessage(error)} testId="trading-messages-error" />}
          {isSuccess && (
            <div className="col-span-6 xl:col-span-12 flex items-center w-full justify-between ">
              <div className="flex items-center px-4 gap-2">
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>
                  <FormattedMessage id="trading.messages" defaultMessage="Messages" />
                </Typography>
                <Button
                  icon="More"
                  onClick={handleMenuOpen}
                  aria-label="more options"
                  aria-controls="messages-menu"
                  aria-haspopup="true"
                />
                <Menu id="messages-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  <MenuItem onClick={handleClearAllMessages} disabled={unreadCount === 0}>
                    <FormattedMessage id="trading.messages.mark_all_as_read" />
                  </MenuItem>
                </Menu>
              </div>
              <Chip
                icon={<TbIcon name="AIFilled" className="!text-[#5F4EC1]" />}
                size="medium"
                className="!h-[27px] !font-bold !text-[#5F4EC1] !text-[12px] !px-2.5 !gap-0.5"
                sx={getChipSxStylingForLevel('AI', {
                  '& .MuiChip-iconMedium': {
                    width: '14px !important',
                    height: '14px !important',
                  },
                })}
                label={intl.formatMessage({ id: 'trading.ai.powered' })}
              />
            </div>
          )}
          {isPending && (
            <Tile>
              <MetricsLoadingSpinner />
            </Tile>
          )}
          {isSuccess && messageSections && messageSections.length === 0 && (
            <EmptyState hasActiveFilters={hasActiveFilters} onExploreInsights={() => void navigate('/insights')} />
          )}
          {isSuccess &&
            messageSections
              ?.filter(section => section.isLatest)
              .map((section, i) => (
                <div className="bg-turquoise-500 bg-opacity-[.08] p-4 rounded-xl col-span-6 xl:col-span-12" key={i}>
                  <div className="flex justify-between items-center pb-[10px]">
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 300 }}>
                      <FormattedMessage id="trading.message.latest.generated.today" />
                    </Typography>
                  </div>
                  <div className="flex flex-col gap-3">
                    {section.groups.map(group => (
                      <div key={group.label} className="flex flex-col gap-3 pb-4">
                        {group.messages?.map(msg => (
                          <TradingMessageDisplay message={msg} allowNavigate key={msg.messageId} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          {isSuccess &&
            messageSections
              ?.filter(section => !section.isLatest)
              .map((section, i) => (
                <div className="xl:col-span-12 col-span-6 bg-snow-200 p-4 rounded-xl" key={i}>
                  <div className="flex flex-col gap-0">
                    {section.groups.map((group, index) => (
                      <div key={group.label}>
                        <div className={cn('pb-[10px] text-sm', index > 0 && 'pt-1.5')}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 300 }}>{group.label}</Typography>
                        </div>
                        <div className="flex flex-col gap-3 pb-4">
                          {group.messages?.map(msg => (
                            <TradingMessageDisplay message={msg} allowNavigate key={msg.messageId} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
        </PageContainer>
      </div>
      {isSuccess && messageSections.length > 0 && (
        <div className="flex flex-row items-center justify-center gap-2 text-text-disabled text-sm">
          <TbIcon name="InformationOutlined" size="lg" className="flex-shrink-0" />
          <FormattedMessage id="trading.message.older.removal" />
        </div>
      )}
    </div>
  );
}
